import { ObjectHelpers } from "../helpers/object-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { TradeOrderDataModel } from "../models/draw-trade-order/trade-order-data.model";
import { IComponent } from "./component.interface";
import { InformationPopupComponent } from "./information-popup.component";

export class TradePlotLineComponent implements IComponent {
    public id = '';

    public moveMode = false;
    public selected = false;

    private base: IChartContainer;
    public model: TradeOrderDataModel;
    private plotLineElement: any = null;
    private plotLineCloneElement: any = null;
    private cloneId = '';
    private showTop = false;
    private deleted = false;
    private mouseUpHandler: EventListener; 

    constructor(base: IChartContainer, tradeOrder: TradeOrderDataModel) {
        this.base = base;
        this.model = tradeOrder;
        this.id = this.model.getClassName();
        this.cloneId = this.id + '-clone';
        this.mouseUpHandler = this.mouseUp.bind(this);

        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.setIconPosition();
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.setIconPosition();
        });
        
        this.init();
        this.onEvents();
        this.onBodyClick();
        this.onDeletePress();
    }

    public remove() {
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        this.deleted = true;
        yAxis.removePlotLine(this.id);
        yAxis.removePlotLine(this.cloneId);
        this.removeStyle();

        this.base.tiChartSeries.removeSubscriberByKey(this.id);
        this.base.domEvents.removeSubscribersByKey(this.id);
        this.base.tiChartSeries.element.removeEventListener('mouseup', this.mouseUpHandler);
    }

    public update(tradeOrder: TradeOrderDataModel) {
        const previousTradeData: TradeOrderDataModel = ObjectHelpers.copy(this.model);
        this.model = tradeOrder;
        if (!this.plotLineElement || this.model.price != previousTradeData.price || this.model.allowMoving != previousTradeData.allowMoving) {
            this.reCreatePlotline();
        }
    }

    private removeStyle() {
        const highChartStyle = this.base.tiChartSeries?.element?.findFirst('.style-' + this.id);
        if (highChartStyle) {
            highChartStyle.remove();
        }
    }

    private createSytle() {
        this.removeStyle();
        const top = this.showTop ? '' : ' margin-top: 18px !important;';
        const className = this.id;
        let styleTag = `<style>`;
        if (this.model.allowMoving) {
            styleTag += `.highcharts-plot-line.${className} {stroke-width: 20px !important; stroke: transparent; cursor: move;}`;
            styleTag += `.highcharts-plot-line.${className}-clone {stroke: ${this.model.color.toRGB()};}`;
            styleTag += `.highcharts-plot-line-label.${className} {color: ${this.model.color.toRGB()}; ${top}}`;
        } else {
            styleTag += `.highcharts-plot-line.${className} {stroke-width: 20px !important; stroke: transparent; cursor: default;}`;
            styleTag += `.highcharts-plot-line.${className}-clone {stroke: ${this.model.color.toRGBA(0.3)};}`;
            styleTag += `.highcharts-plot-line-label.${className} {color: ${this.model.color.toRGBA(0.3)}; opacity: 0.5; ${top}}`;
        }

        styleTag += `.highcharts-plot-line-label.${className} {backdrop-filter: blur(10px); z-index: 25; padding-left: 12px}`;

        styleTag += `</style>`;

        const chartStyleContainer = `<div class="style-${className}">${styleTag}</div>`;
        this.base.tiChartSeries?.element.insertAdjacentHTML('afterbegin', chartStyleContainer);
    }

    private init() {
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        const yAxis: any = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        const orderProfit = this.model.getOrderProfitAnnotation(chartStyle?.isDarkTheme ?? false);
        this.showTop = orderProfit.showTop;
        this.createSytle();
        if (yAxis) {
            yAxis.addPlotLine({
                value: this.model.price,
                className: this.id,
                id: this.id,
                zIndex: 12,
                label: {
                    formatter: () => {
                        return this.model.getBriefOrderDescription(null) + ' ' + orderProfit.text;
                    },
                    className: this.id,
                    useHTML: true
                },
                events: {
                    mouseover: () => {
                        this.showTooltip();
                    },
                    mousedown: () => {
                        if (!this.model.allowMoving) {
                            return;
                        }

                        this.base.tiChartSeries.preventDragging = true;
                        this.moveMode = true;
                    }
                }
            });

            yAxis.addPlotLine({
                value: this.model.price,
                className: this.cloneId,
                id: this.cloneId,
                zIndex: 11
            });

            this.setIconPosition();
            setTimeout(() => {
                this.setIconPosition();
                
                setTimeout(() => {
                    this.bindEvents();
                }, 800);
            }, 100);
        }
    }

    private reCreatePlotline() {
        const yAxis: any = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return;
        }

        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        const orderProfit = this.model.getOrderProfitAnnotation(chartStyle?.isDarkTheme ?? false);
        this.showTop = orderProfit.showTop;
        this.createSytle();

        yAxis.removePlotLine(this.id);
        yAxis.addPlotLine({
            value: this.model.price,
            className: this.id + '  selected',
            id: this.id,
            zIndex: 12,
            label: {
                formatter: () => {
                    return this.model.getBriefOrderDescription(this.model.price) + ' ' + orderProfit.text;
                },
                className: this.id,
                useHTML: true,
            },
            events: {
                mouseover: () => {
                    this.showTooltip();
                },
                mousedown: () => {
                    if (!this.model.allowMoving) {
                        return;
                    }

                    this.base.tiChartSeries.preventDragging = true;
                    this.moveMode = true;
                }
            }
        });

        yAxis.removePlotLine(this.cloneId);
        yAxis.addPlotLine({
            value: this.model.price,
            className: this.cloneId,
            id: this.cloneId,
            zIndex: 11
        });
        
        this.setIconPosition();
    }

    private mouseUp() {
        if (!this.moveMode || !this.model.allowMoving) {
            return;
        }

        this.moveMode = false;
        setTimeout(() => {
            this.base.tiChartSeries.preventDragging = false;
        }, 500);
        
        this.unselectPlotLine();
        this.base.chartService.requestUpdateChartTrade(this.model.id, this.model.price);
    }

    private onEvents() {
        this.base.domEvents.onMouseMove(this.id, (event: any) => {
            if (this.deleted || !this.moveMode || !this.base.tiChartSeries) {
                return;
            }

            const yAxis: any = this.base.tiChartSeries.highStockChart.yAxis[0];
            if (!yAxis) {
                return;
            }

            const pointer = this.base.tiChartSeries.highStockChart.pointer.normalize(event);
            const parentElementRect = this.base.tiChartSeries.chartElement.getBoundingClientRect();
            const yTop = pointer.y - parentElementRect.y;

            const yValue = yAxis.toValue(yTop);
            this.model.price = yValue;

            if (this.model.stopPrice > 0) {
                this.model.stopPrice = yValue;
            } else if (this.model.limitPrice > 0) {
                this.model.limitPrice = yValue;
            }

            this.reCreatePlotline();

            this.bindEvents();
        });

        this.base.tiChartSeries.element.addEventListener('mouseup',  this.mouseUpHandler);
    }

    private onBodyClick() {
        this.base.domEvents.onScreenClick(this.id, (event: any) => {
            if (this.deleted || !this.model.allowMoving) {
                return;
            }

            if (!event.target || !event.target.classList.contains(this.id)) {
                this.unselectPlotLine();
            } else {
                this.selectPlotLine();
            }
        });
    }

    private onDeletePress() {
        this.base.domEvents.onDeletePress(this.id, () => {
            if (this.deleted || this.base.marketingWebChartMode) {
                return;
            }
            
            if (this.selected) {
                this.base.chartService.requestRemoveChartTrade(this.model.id);
                this.unselectPlotLine();
            }
        });
    }

    private selectPlotLine() {
        this.plotLineElement = this.base.tiChartSeries.element.findFirst('.' + this.id);
        this.plotLineCloneElement = this.base.tiChartSeries.element.findFirst('.' + this.id + '-clone');

        if (!this.model.allowMoving) {
            return;
        }

        this.selected = true;
        this.plotLineElement && this.plotLineElement.classList.add('selected');
        this.plotLineCloneElement && this.plotLineCloneElement.classList.add('selected');
    }

    private unselectPlotLine() {
        this.plotLineElement = this.base.tiChartSeries.element.findFirst('.' + this.id);
        this.plotLineCloneElement = this.base.tiChartSeries.element.findFirst('.' + this.id + '-clone');

        this.selected = false;
        this.plotLineElement && this.plotLineElement.classList.remove('selected');
        this.plotLineCloneElement && this.plotLineCloneElement.classList.remove('selected');
    }

    private bindEvents() {
        this.plotLineElement = this.base.tiChartSeries.element.findFirst('.' + this.id);
        this.plotLineCloneElement = this.base.tiChartSeries.element.findFirst('.' + this.id + '-clone');
        if (!this.model.allowMoving) {
            return;
        }

        if (this.selected) {
            this.selectPlotLine();
        }
    }

    private setIconPosition() {
        const chartExtraSmall = this.base.isExtraSmall();
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        this.base.tiChartSeries.element.findFirst(`.highcharts-plot-line-label.${this.id}`)?.ifExistThen((el: HTMLElement) => {
            el.querySelectorAll('img').forEach((i: HTMLImageElement) => {
                i.remove();
            });
            let left = this.base.tiChartSeries.element.clientWidth - (chartStyle?.showSmartRiskLevel ? 118 : 88);
            const top = this.showTop ? 7 : -10;

            if (chartExtraSmall) {
                left = this.base.tiChartSeries.element.clientWidth - 40;
            }

            const style = `width: 15px; height: 16px;position: absolute;left: ${left}px; top: ${top}px`;
            const tooltip = this.model.getOrderDescriptionTooltip();

            if (this.model.allowMoving) {
                if (this.model.isLong) {
                    el.innerHTML += `<img src="${this.base.basePath}assetfiles/img/B-v2.svg" style="${style}" title="${tooltip}"/>`;
                } else {
                    el.innerHTML += `<img src="${this.base.basePath}assetfiles/img/S-v2.svg" style="${style}" title="${tooltip}"/>`;
                }

                if (this.base.marketingWebChartMode) {
                    el.querySelectorAll('.remove').forEach((el: Element) => {
                        el.remove();
                    });

                    el.innerHTML += `
                    <div class="remove">
                        <svg style="color:#fff;cursor:pointer;background:${this.model.color.toRGB()};width:10px;height:10px;position: absolute;left: ${left - 25}px; top: ${this.showTop ? '11' : '-6'}px;" class="remove" >
                            <use href="${this.base.basePath}assetfiles/img/closev2.svg#closev2"></use>
                        </svg>
                    </div>`;


                    setTimeout(() => {
                        el.findFirst('.remove')?.ifExistThen((b: HTMLElement) => {
                            if (b.containClass('inited')) {
                                return;
                            }
                            b.addClass('inited');
                            b.addEventListener('click', (e: any) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const popup = this.base.getComponentsByType(InformationPopupComponent)[0] as InformationPopupComponent;
                                popup?.show(`Cancel Order`, this.model.getCancelOrderMessage(), () => {
                                    this.base.chartService.requestRemoveChartTrade(this.model.id);
                                    this.remove();
                                });
                            });
                        });
                    }, 300);
                }
            }
        });
    }

    private showTooltip() {
        if (this.plotLineElement) {
            const titleElement = document.createElementNS('http://www.w3.org/2000/svg', 'title');
            const tooltip = this.model.getOrderDescriptionTooltip();
            titleElement.innerHTML = tooltip;

            const plotLineElement = this.plotLineElement as SVGPathElement;
            const groupElement = plotLineElement.parentNode;
            if (groupElement) {
                groupElement.querySelectorAll('title').forEach((t: Element) => {
                    t.remove();
                });
                groupElement?.append(titleElement);
            }
        }
    }
}